import { Box, BoxProps, Skeleton, styled, Typography } from '@mui/material';
import { useMemo } from 'react';
import { LwCardProps } from './card.types';

const colors = { red: '#CC4400', green: '#00A36D', orange: '#FF7B00' };

export const LwCard = (card: LwCardProps) => {
  const cardValueColor = useMemo(
    () => `${getColors(card.threshold, card.primaryValue)}`,
    [card.threshold, card.primaryValue]
  );
  const isLoading = !card.primaryValue || !card.secondaryValue;
  return (
    <Card isGradient={card.isGradient}>
      <TitleRow>
        <Typography variant="h2" noWrap>
          {card.primaryText}
        </Typography>
        <Typography noWrap variant="subtitle2">
          {card.status}
        </Typography>
      </TitleRow>
      <Box>
        {card.subheaderText ? (
          <Typography variant="body2">{card.subheaderText}</Typography>
        ) : (
          <PlaceholderBox />
        )}
      </Box>
      <Box>
        <PrimaryValueTypography $color={cardValueColor}>
          {isLoading ? <Skeleton height={24} width={100} /> : card.primaryValue}
          {!isLoading && card.isFillRateCard ? <StyledPercentageText /> : ''}
        </PrimaryValueTypography>
      </Box>
      <SecondaryDataRow>
        <Typography variant="h2" noWrap>
          {isLoading ? <Skeleton height={20} width={100} /> : card.secondaryValue}
        </Typography>
        <Typography noWrap variant="subtitle2">
          {card.secondaryText}
        </Typography>
      </SecondaryDataRow>
    </Card>
  );
};

const Card = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isGradient',
})<BoxProps & { isGradient?: boolean }>(({ isGradient, theme }) => ({
  borderRadius: '32px',
  padding: '24px',
  boxShadow: '0px 4px 6px 0px rgba(51, 51, 51, 0.03),0px 0px 8px 0px rgba(51, 51, 51, 0.03)',
  background: isGradient
    ? `linear-gradient(to right, ${theme.palette.lwPrimaryAccentLight['20']}, ${theme.palette.lwPrimaryAccentDark['10']})`
    : 'White',
}));

const TitleRow = styled(Box)<BoxProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(2),
  alignItems: 'baseline',
}));

const PrimaryValueTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$color',
})<{ $color?: string }>(({ theme, $color }) => ({
  '&.MuiTypography-root': {
    fontSize: '36px',
    marginTop: theme.spacing(2),
    fontWeight: 700,
    color: $color,
  },
}));

const PlaceholderBox = styled(Box)<BoxProps>(() => ({
  height: '20px',
}));

const SecondaryDataRow = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const StyledPercentageText = () => {
  return <span style={{ fontSize: '24px' }}>%</span>;
};

const getColors = (
  analyticsThreshold: { low: number; high: number; thresholdInverted?: boolean },
  value: string
) => {
  const parsedValue = parseFloat(value);
  const { low, high, thresholdInverted } = analyticsThreshold;

  if (thresholdInverted) {
    if (parsedValue < low) {
      return colors.green;
    }
    if (parsedValue > high) {
      return colors.red;
    }
  }
  if (parsedValue < low) {
    return colors.red;
  }
  if (parsedValue > high) {
    return colors.green;
  }
  return colors.orange;
};
